import { useEffect, useRef } from "react";
import * as d3 from "d3";

interface OverlappingDonutChartProps {
  arisePercent: number;
  otherPercent: number;
  totalPercent: number;
  size: number;
}

export default function OverlappingDonutChart({
  arisePercent,
  otherPercent,
  totalPercent,
  size,
}: OverlappingDonutChartProps) {
  const ref = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    const svgEl = d3.select(ref.current);
    svgEl.selectAll("*").remove();

    const width = size;
    const height = size;
    const radius = Math.min(width, height) / 2;
    const thickness = Math.min(50, radius * 0.6);
    const innerRadius = radius - thickness;

    const svg = svgEl
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    // Compute overlap and unique part
    const overlap = arisePercent + otherPercent - totalPercent;
    const ariseUnique = arisePercent - overlap;
    const otherUnique = otherPercent - overlap;

    // Compute start and end percentages for each arc
    const otherStart = 0;
    const otherEnd = otherStart + otherPercent;
    // Arise arc: starts AFTER the portion that is not shared by others
    const ariseStart = otherStart + otherUnique;
    const ariseEnd = ariseStart + ariseUnique + overlap;

    // Convert percentage directly to radians
    const percentToRad = (p: number) => (p / 100) * 2 * Math.PI;

    const otherEndAngle = percentToRad(otherEnd);
    const ariseStartAngle = percentToRad(ariseStart);
    const ariseEndAngle = percentToRad(ariseEnd);

    // Background ring
    const backgroundArc = d3.arc().innerRadius(innerRadius).outerRadius(radius);

    svg
      .append("path")
      .attr(
        "d",
        backgroundArc({
          startAngle: 0,
          endAngle: 2 * Math.PI,
          innerRadius: innerRadius,
          outerRadius: radius,
        })!,
      )
      .attr("fill", "#EEEFF5");

    if (window.innerWidth >= 768) {
      svg
        .append("text")
        .attr("text-anchor", "middle")
        .attr("font-weight", "700")
        .attr("transform", "translate(0, 4)")
        .attr("class", `chart-value`)
        .text(totalPercent + "%");
    }
    // Other organizations
    const otherOrganizationsArc = d3
      .arc()
      .innerRadius(innerRadius)
      .outerRadius(radius - thickness / 2);

    svg
      .append("path")
      .attr(
        "d",
        otherOrganizationsArc({
          startAngle: otherStart,
          endAngle: otherEndAngle,
          innerRadius: innerRadius,
          outerRadius: radius - thickness / 2,
        })!,
      )
      .attr("fill", "#47397B");
    // Arise
    const ariseArc = d3.arc().innerRadius(innerRadius).outerRadius(radius);

    svg
      .append("path")
      .attr(
        "d",
        ariseArc({
          startAngle: ariseStartAngle,
          endAngle: ariseEndAngle,
          innerRadius: innerRadius,
          outerRadius: radius,
        })!,
      )
      .attr("fill", "#009FE380");
  }, [arisePercent, otherPercent, totalPercent, size]);

  return <svg ref={ref} viewBox={`0 0 ${size} ${size}`}></svg>;
}
